import { useAuth } from '../contexts/AuthContext';

// Role-based access control hook
export const useRoleAccess = () => {
  const { user, isAdmin, isTeacher } = useAuth();
  
  const userRole = user?.userRole;
  
  // Define role permissions
  const permissions = {
    admin: {
      canViewAllUsers: true,
      canCreateUsers: true,
      canEditUsers: true,
      canDeleteUsers: true,
      canViewAllClasses: true,
      canCreateClasses: true,
      canEditClasses: true,
      canDeleteClasses: true,
      canViewAllStudents: true,
      canCreateStudents: true,
      canEditStudents: true,
      canDeleteStudents: true,
      canViewAllAttendance: true,
      canMarkAttendance: true,
      canViewAllResults: true,
      canCreateResults: true,
      canEditResults: true,
      canDeleteResults: true,
      canViewAllFees: true,
      canCreateFees: true,
      canEditFees: true,
      canViewAllNotices: true,
      canCreateNotices: true,
      canEditNotices: true,
      canDeleteNotices: true,
      canViewAllCalendar: true,
      canCreateCalendar: true,
      canEditCalendar: true,
      canDeleteCalendar: true,
      canViewSettings: true,
      canEditSettings: true,
      canViewReports: true,
      canSendMessages: true,
      canViewDashboard: true,
    },
    teacher: {
      canViewAllUsers: false,
      canCreateUsers: false,
      canEditUsers: false,
      canDeleteUsers: false,
      canViewAllClasses: false,
      canCreateClasses: false,
      canEditClasses: false,
      canDeleteClasses: false,
      canViewAllStudents: false,
      canCreateStudents: true,
      canEditStudents: true,
      canDeleteStudents: false,
      canViewAllAttendance: false,
      canMarkAttendance: true,
      canViewAllResults: false,
      canCreateResults: true,
      canEditResults: true,
      canDeleteResults: false,
      canViewAllFees: false,
      canCreateFees: false,
      canEditFees: false,
      canViewAllNotices: true,
      canCreateNotices: false,
      canEditNotices: false,
      canDeleteNotices: false,
      canViewAllCalendar: true,
      canCreateCalendar: false,
      canEditCalendar: false,
      canDeleteCalendar: false,
      canViewSettings: false,
      canEditSettings: false,
      canViewReports: false,
      canSendMessages: true,
      canViewDashboard: true,
    }
  };

  // Get permissions for current user
  const userPermissions = permissions[userRole] || {};

  // Helper functions
  const hasPermission = (permission) => {
    return userPermissions[permission] || false;
  };

  const hasAnyPermission = (permissionList) => {
    return permissionList.some(permission => hasPermission(permission));
  };

  const hasAllPermissions = (permissionList) => {
    return permissionList.every(permission => hasPermission(permission));
  };

  // Role-specific data access
  const getAccessibleData = (data, dataType) => {
    if (isAdmin) {
      return data; // Admin can see everything
    }
    
    if (isTeacher) {
      switch (dataType) {
        case 'students':
          // Teachers can only see students from their assigned classes
          return data.filter(item => 
            item.assignedTeacher === user?._id || 
            item.classes?.some(cls => cls.teacher === user?._id)
          );
        case 'attendance':
          // Teachers can only see attendance for their classes
          return data.filter(item => 
            item.class?.teacher === user?._id
          );
        case 'results':
          // Teachers can only see results for their subjects/classes
          return data.filter(item => 
            item.subject?.teacher === user?._id ||
            item.class?.teacher === user?._id
          );
        case 'notices':
          // Teachers can see all notices (read-only)
          return data;
        case 'calendar':
          // Teachers can see all calendar events (read-only)
          return data;
        default:
          return [];
      }
    }
    
    return [];
  };

  // Navigation items based on role
  const getNavigationItems = () => {
    const baseItems = [
      { path: '/dashboard', label: 'Dashboard', icon: 'LayoutDashboard', permission: 'canViewDashboard' },
      { path: '/profile', label: 'Profile', icon: 'User', permission: 'canViewDashboard' },
    ];

    const roleSpecificItems = [
      // Student Management
      { path: '/students', label: 'Students', icon: 'Users', permission: 'canViewAllStudents', roles: ['admin', 'teacher'] },
      { path: '/parents', label: 'Parents', icon: 'UserCheck', permission: 'canViewAllStudents', roles: ['admin', 'teacher'] },
      
      // Academic Operations
      { path: '/classes', label: 'Classes', icon: 'BookOpen', permission: 'canViewAllClasses', roles: ['admin'] },
      { path: '/exams', label: 'Exams', icon: 'FileText', permission: 'canViewAllResults', roles: ['admin', 'teacher'] },
      { path: '/exam-results', label: 'Exam Results', icon: 'BarChart3', permission: 'canViewAllResults', roles: ['admin', 'teacher'] },
      { path: '/test-results', label: 'Test Results', icon: 'Clipboard', permission: 'canViewAllResults', roles: ['admin', 'teacher'] },
      
      // Attendance & Communication
      { path: '/attendance', label: 'Attendance', icon: 'Calendar', permission: 'canMarkAttendance', roles: ['admin', 'teacher'] },
      { path: '/notices', label: 'Notices', icon: 'Bell', permission: 'canViewAllNotices', roles: ['admin', 'teacher'] },
      
      // Calendar & Events
      { path: '/calendar', label: 'Calendar', icon: 'CalendarDays', permission: 'canViewAllCalendar', roles: ['admin', 'teacher'] },
      
      // Financial Management (reminders merged into Fees page)
      
      // Administration (Admin Only)
      { path: '/users', label: 'Users', icon: 'Users', permission: 'canViewAllUsers', roles: ['admin'] },
      { path: '/settings', label: 'Settings', icon: 'Settings', permission: 'canViewSettings', roles: ['admin'] },
    ];

    return [...baseItems, ...roleSpecificItems].filter(item => {
      if (item.roles && !item.roles.includes(userRole)) return false;
      return hasPermission(item.permission);
    });
  };

  return {
    userRole,
    isAdmin,
    isTeacher,
    permissions: userPermissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getAccessibleData,
    getNavigationItems,
  };
};

export default useRoleAccess;
